/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Form, Button, Card, Alert } from 'react-bootstrap';

import { addError, addWarning, addSuccess } from 'rdx/alert';
import { Ctx } from 'components/CtxFormFields/CtxInputs';
import useErrorState, { translateError } from 'utils/hooks/useErrorState';
import RenderFormItem from 'components/CtxFormFields/FormFieldCtx';

import { netsCloudDefault, netsCloudFields } from './formShape';
import nets from './requests';

function NetsCloud() {
  const dispatch = useDispatch();
  const { level } = useParams();

  const formContext = useContext(Ctx);
  const { integrationState, setEnabledSave } = formContext.values;

  const validationObject = {
    ...netsCloudFields.account,
    ...netsCloudFields.pos,
  };
  // Allow saving account crdentials on levels above POS
  if (level !== 'Pos') {
    delete validationObject.terminal;
  }

  const values = useMemo(
    () => ({ ...netsCloudDefault, ...integrationState }),
    [netsCloudDefault, integrationState],
  );

  const { errors, hasErrors } = useErrorState(values, validationObject);
  const { username, password, terminal } = values;

  const onChange = (key, value) => {
    formContext.onChange({ [key]: value });
  };

  const [token, setToken] = useState();

  const [terminals, setTerminals] = useState([]);
  // If there is selected terminal and user hasn't logged in yet,
  // add it to terminal select dropdown options
  useEffect(() => {
    if (terminal && !token && !terminals.length) {
      setTerminals([terminal]);
    }
  }, [integrationState]);
  const options = terminals.map((op) => ({ name: op, value: op }));

  // if all required fields are present and validated, enable save button
  useEffect(() => {
    setEnabledSave(!hasErrors);
  }, [errors]);

  const netsLogin = async () => {
    if (token) return setToken(null);
    if (username && password) {
      try {
        const { data } = await nets.login(username, password);
        dispatch(addSuccess('Login successful'));
        if (data) {
          if (data.token) {
            setToken(data.token);
          }
          if (data.terminals) {
            setTerminals(data.terminals);
          }
          return true;
        }
        return dispatch(
          addError('No authentication token in the response body.'),
        );
      } catch (err) {
        console.error(err);
        if (err.response && err.response.data.error) {
          return dispatch(addWarning(err.response.data.error));
        }
        return dispatch(addError('Login failed'));
      }
    } else {
      return dispatch(
        addWarning(
          'Username and Password are needed to login to Nets Finland.',
        ),
      );
    }
  };

  return (
    <div className="d-flex flex-column flex-fill">
      {level !== 'Pos' && (
        <Alert variant="warning">
          Nets Finland Cloud requires selection of a Terminal. It is specific
          for each POS integration. In order to finish the integration, go to
          the POS you would like to use and select the Terminal there.
        </Alert>
      )}
      <Ctx.Provider
        value={useMemo(() => ({ values, onChange }), [values, onChange])}
      >
        <Card className="my-3">
          <Card.Header>Nets Account Credentials</Card.Header>
          <Card.Body>
            <Form>
              {Object.values(netsCloudFields.account).map(
                ({ validate, ...rest }) => (
                  <RenderFormItem
                    key={rest.id}
                    disabled={!!token}
                    size="md"
                    name={rest.id}
                    error={translateError(errors[rest.id])}
                    {...rest}
                  />
                ),
              )}
              <div className="d-flex justify-content-between mt-auto">
                <Button variant="warning" size="sm" onClick={netsLogin}>
                  {token ? 'Edit' : 'Login'}
                </Button>
                {token && (
                  <Button disabled variant="success" size="sm">
                    Logged in
                  </Button>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
        {level === 'Pos' && (
          <Card className="flex-fill mb-3">
            <Card.Header>Select Device</Card.Header>
            <Card.Body className="d-flex">
              <Form className="d-flex flex-column flex-fill">
                <RenderFormItem
                  type="select"
                  size="md"
                  name="terminal"
                  options={[
                    ...netsCloudFields.pos.terminal.options,
                    ...options,
                  ]}
                  error={translateError(errors.terminal)}
                />
              </Form>
            </Card.Body>
          </Card>
        )}
      </Ctx.Provider>
    </div>
  );
}

export default NetsCloud;
